import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  IconButton,
  Divider,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatAPI, styleAPI, recommendationsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import ChatLimits from '../components/Layout/ChatLimits';
import { debugLog } from '../utils/debug';

// Suggested questions to ask the AI
const suggestedQuestions = [
  "What should I wear today?",
  "Help me create an outfit for a job interview",
  "Suggest outfits based on my style preferences",
  "What are the current fashion trends?",
  "How can I style this black shirt?",
  "What accessories go well with this outfit?",
];

// Add styling for images in Markdown content
const markdownComponents = {
  img: (props) => <ImageComponent {...props} />,
};

// Separate component for handling images with hooks
const ImageComponent = ({ ...props }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };
  
  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    if (process.env.NODE_ENV !== 'production') {
      // Helpful for debugging storage/auth issues without spamming prod consoles.
      console.error('Failed to load image:', props.src);
    }
  };

  // Add authentication token to image requests if needed
  const getImageSrc = (src) => {
    // Handle null/undefined/empty sources
    if (!src || typeof src !== 'string' || src.trim() === '') {
      return '';
    }
    
    // -- 1. Normalize internal sandbox paths --
    let normalizedSrc = src.trim();
    if (normalizedSrc.startsWith('sandbox:')) {
      // `sandbox:` is a prefix added by the LLM/agent for non-http internal paths.
      // Strip it so the rest of the logic treats the path as a normal relative url.
      normalizedSrc = normalizedSrc.replace(/^sandbox:/, '');
    }

    // -- 2. External URLs (OpenAI CDN, placeholders, etc.) --
    if (normalizedSrc?.includes('http') && !normalizedSrc.includes(window.location.hostname)) {
      return normalizedSrc;
    }

    // -- 3. Internal API image paths --
    if (normalizedSrc?.startsWith('/api/images/') || normalizedSrc?.startsWith('/api/uploads/')) {
      const token = localStorage.getItem('auth_token');

      // Determine backend base URL from env or default
      const backendUrl =
        process.env.REACT_APP_API_URL ||
        process.env.NEXT_PUBLIC_API_URL ||
        'http://localhost:5001';

      // Build absolute URL
      const fullUrl = `${backendUrl}${normalizedSrc}`;

      if (token) {
        const separator = fullUrl.includes('?') ? '&' : '?';
        const finalUrl = `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
        return finalUrl;
      }

      return fullUrl;
    }

    // -- 4. Fallback --
    return normalizedSrc;
  };

  if (imageError) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          textAlign: 'center',
          margin: '8px auto',
          maxWidth: '100%'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Unable to load image. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', margin: '8px auto', maxWidth: '100%' }}>
      {imageLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      <img 
        {...props}
        src={getImageSrc(props.src)}
        alt={props.alt || 'Generated outfit image'}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{ 
          maxWidth: '100%', 
          height: 'auto', 
          borderRadius: '8px',
          display: 'block',
          margin: '0 auto',
          opacity: imageLoading ? 0.3 : 1,
          transition: 'opacity 0.3s ease'
        }} 
      />
    </Box>
  );
};

const Chat = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const chatNavigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [stylePreferences, setStylePreferences] = useState([]);
  const [styleProfileError, setStyleProfileError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [groundingItemIds, setGroundingItemIds] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isMountedRef = useRef(true);
  const recSeededRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Add welcome message when component mounts
  useEffect(() => {
    // Log user information for debugging
    if (user) {
      debugLog('Chat component mounted with authenticated user id:', user.id);
    } else {
      debugLog('Chat component mounted with no authenticated user');
    }

    const welcomeMessage = {
      id: "welcome",
      sender: "ai",
      content: `Hello${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! I'm your FashionMate AI assistant. How can I help with your style today?`,
      timestamp: new Date().toISOString(),
    };

    if (isMountedRef.current) {
      setMessages([welcomeMessage]);
    }

    // Fetch style preferences if user is authenticated
    if (isAuthenticated && user) {
      debugLog('Fetching style preferences for authenticated user');
      fetchStylePreferences();
    } else {
      debugLog('Skipping style preferences fetch for unauthenticated user');
    }
  }, [user, isAuthenticated]);

  // Seed chat from recommendation cards and planning quick actions.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const recId = params.get('rec');
    const prompt = params.get('prompt');
    if ((!recId && !prompt) || recSeededRef.current) return;
    recSeededRef.current = true;

    // Clear the URL param
    chatNavigate('/chat', { replace: true });

    if (prompt) {
      setInputValue(prompt);
      setShowSuggestions(false);
      return;
    }

    (async () => {
      try {
        const res = await recommendationsAPI.get(recId);
        const starter = res?.data?.data?.starter_message;
        const sourceItemIds = res?.data?.data?.source_item_ids || [];
        if (starter && isMountedRef.current) {
          setInputValue(starter);
          setGroundingItemIds(sourceItemIds);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error('Failed to load recommendation for chat seeding', err);
      }
    })();
  }, [location.search, chatNavigate]);

  // Fetch user's style preferences
  const fetchStylePreferences = async () => {
    try {
      debugLog('Attempting to fetch style preferences...');
      const response = await styleAPI.getPreferences();
      debugLog('Style preferences for chat:', response.data);

      if (!isMountedRef.current) return;

      // Check if what we got back looks like a login redirect
      if (response.data.login_url) {
        debugLog('Received login URL instead of preferences, user may not be properly authenticated');
        setStylePreferences([]);
      } else if (response.data.data) {
        setStylePreferences(response.data.data || []);
      } else {
        debugLog('Unexpected format in style preferences response:', response.data);
        setStylePreferences([]);
      }

      setStyleProfileError(null);
    } catch (err) {
      console.error('Error fetching style preferences for chat:', err);
      if (isMountedRef.current) {
        setStyleProfileError('Failed to load style preferences');
        setStylePreferences([]);
      }
    }
  };

  // Format style preferences for AI context
  const formatStylePreferencesForAI = () => {
    if (!stylePreferences || stylePreferences.length === 0) {
      return null;
    }

    // Group preferences by category
    const preferencesByCategory = {};
    stylePreferences.forEach(pref => {
      if (!preferencesByCategory[pref.category]) {
        preferencesByCategory[pref.category] = [];
      }
      preferencesByCategory[pref.category].push({
        preference: pref.preference,
        importance: pref.importance
      });
    });

    // Format as a string for the AI
    let formattedPreferences = "User Style Profile:\n";
    
    Object.entries(preferencesByCategory).forEach(([category, prefs]) => {
      formattedPreferences += `- ${category}:\n`;
      
      prefs.forEach(pref => {
        const importanceLabel = getImportanceLabel(pref.importance);
        formattedPreferences += `  • ${pref.preference} (Importance: ${importanceLabel})\n`;
      });
    });
    
    return formattedPreferences;
  };
  
  // Helper to get readable importance label
  const getImportanceLabel = (value) => {
    const labels = {
      1: 'Low',
      2: 'Somewhat Important',
      3: 'Important',
      4: 'Very Important',
      5: 'Essential'
    };
    return labels[value] || 'Normal';
  };

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    const userMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      content: inputValue,
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);
    // Hide suggestions after sending a message
    setShowSuggestions(false);
    
    try {
      // Format chat history for the backend
      const formattedHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));
      
      // Format style preferences for the AI (only if authenticated)
      const styleProfileContext = isAuthenticated && user ? formatStylePreferencesForAI() : null;
      debugLog('Including style profile in chat:', styleProfileContext ? 'Yes' : 'No');
      
      // Send message using authenticated endpoint
      debugLog('User authenticated, using private chat endpoint');
      const response = await chatAPI.sendMessage(
        userMessage.content, 
        formattedHistory,
        styleProfileContext,
        groundingItemIds
      );
      
      if (!isMountedRef.current) return;

      const aiMessage = {
        id: `ai-${Date.now()}`,
        sender: 'ai',
        content: response.data.response,
        grounding: response.data.grounding,
        timestamp: new Date().toISOString(),
      };

      const nextGroundingItemIds = response.data.grounding?.source_item_ids;
      if (Array.isArray(nextGroundingItemIds)) {
        setGroundingItemIds(nextGroundingItemIds);
      }

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Customized error message based on error type
      let errorMessage = {
        id: `error-${Date.now()}`,
        sender: 'ai',
        content: "I'm sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toISOString(),
        isError: true,
      };
      
      // Check if there's a custom user message from the API interceptor
      if (error.userMessage) {
        errorMessage.content = error.userMessage;
      }
      // Check if it's a timeout error
      else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage.content = "Sorry, the response is taking longer than expected. The AI is working on a thoughtful response but it's taking a while. Please try again or ask a simpler question.";
        errorMessage.isTimeout = true;
      }
      // Check if it's a network error
      else if (error.code === 'ERR_NETWORK') {
        errorMessage.content = "Sorry, I'm having trouble connecting to the server. Please check your internet connection and try again.";
      }
      
      if (isMountedRef.current) {
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        inputRef.current?.focus({ preventScroll: true });
      }
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const toggleSuggestions = () => {
    setShowSuggestions(prev => !prev);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background:
              'radial-gradient(360px 200px at 80% -20%, rgba(255, 110, 77, 0.2), transparent 65%), linear-gradient(135deg, rgba(14, 123, 255, 0.14), rgba(0, 165, 255, 0.08))',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            AI Stylist Chat
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Ask for outfit ideas, seasonal combinations, event looks, or styling breakdowns based on your closet.
          </Typography>
        </Paper>
        
        {isAuthenticated && <ChatLimits />}
        
        <Paper
          variant="outlined"
          sx={{
            height: { xs: '70vh', md: '68vh' },
            minHeight: { xs: 500, md: 600 },
            display: 'flex',
            flexDirection: 'column',
            mb: 2,
            overflow: 'hidden',
            borderColor: 'divider',
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.background.paper, 0.88),
            backdropFilter: 'blur(14px)',
          }}
        >
          {/* Messages container */}
          <Box
            ref={messagesContainerRef}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: { xs: 1.5, sm: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: alpha(theme.palette.text.primary, 0.015),
            }}
          >
            {/* Messages go here */}
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: isMobile ? '100%' : '80%',
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                {/* Message display code */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  {msg.sender === 'ai' ? (
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 28,
                        height: 28,
                        mr: 1,
                      }}
                    >
                      <SmartToyIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  ) : (
                    <Avatar
                      sx={{
                        bgcolor: 'secondary.main',
                        width: 28,
                        height: 28,
                        mr: 1,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {msg.sender === 'ai' ? 'FashionMate AI' : 'You'} •{' '}
                    {formatTimestamp(msg.timestamp)}
                  </Typography>
                </Box>

                {/* Message content */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    borderColor: msg.isNotice
                      ? alpha(theme.palette.info.main, 0.25)
                      : msg.sender === 'user'
                      ? alpha(theme.palette.primary.main, 0.22)
                      : 'divider',
                    backgroundColor: msg.isNotice
                      ? alpha(theme.palette.info.main, 0.08)
                      : msg.sender === 'user'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'background.paper',
                    ml: msg.sender === 'user' ? 0 : isMobile ? 0 : 4,
                    mr: msg.sender === 'user' ? (isMobile ? 0 : 4) : 0,
                    position: 'relative',
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '100%' : '100%',
                    boxShadow: msg.sender === 'user'
                      ? `0 8px 20px ${alpha(theme.palette.primary.main, 0.12)}`
                      : 'none',
                  }}
                >
                  {/* Content rendering logic */}
                  {msg.isNotice ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <InfoIcon
                        sx={{ fontSize: 20, mr: 1, color: 'info.main' }}
                      />
                      <Typography variant="body2" component="div">
                        {msg.content}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        '& > :first-of-type': { mt: 0 },
                        '& > :last-child': { mb: 0 },
                        '& p': { my: 0.75 },
                        '& ul, & ol': { pl: 2.25, my: 0.75 },
                        '& li': { my: 0.25 },
                        '& a': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                        },
                        '& code': {
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          fontSize: '0.92em',
                          backgroundColor: alpha(theme.palette.text.primary, 0.06),
                          padding: '2px 6px',
                          borderRadius: 1.5,
                        },
                        '& pre code': {
                          backgroundColor: 'transparent',
                          padding: 0,
                        },
                      }}
                    >
                      <ReactMarkdown components={markdownComponents} urlTransform={(url) => url}>
                        {msg.content}
                      </ReactMarkdown>
                      {msg.grounding?.used_closet_items?.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                            Grounded in your closet
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.75 }}>
                            {msg.grounding.used_closet_items.map((item) => (
                              <Chip
                                key={item.id}
                                size="small"
                                label={`${[item.color, item.subcategory || item.category].filter(Boolean).join(' ')}${
                                  msg.grounding.visual_reference_item_ids?.includes(item.id)
                                    ? ' • visual source'
                                    : ''
                                }`}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            ))}
            {styleProfileError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                icon={<ErrorIcon fontSize="inherit" />}
              >
                {styleProfileError}
              </Alert>
            )}
            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  alignSelf: 'flex-start',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 28,
                    height: 28,
                    mr: 1,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    ml: 4,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    FashionMate AI is thinking...
                  </Typography>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Divider */}
          <Divider />

          {/* Input area */}
          <Box
            component="form"
            onSubmit={handleSendMessage}
            sx={{
              p: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
            }}
          >
            <TextField
              fullWidth
              placeholder="Type your message..."
              value={inputValue}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              disabled={loading}
              inputRef={inputRef}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                },
              }}
            />
            <IconButton
              type="submit"
              disabled={!inputValue.trim() || loading}
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                border: '1px solid',
                borderColor: inputValue.trim() ? 'transparent' : 'divider',
                color: inputValue.trim() ? 'common.white' : 'text.secondary',
                backgroundImage: inputValue.trim()
                  ? 'linear-gradient(135deg, #0E7BFF 0%, #00A5FF 100%)'
                  : 'none',
                backgroundColor: inputValue.trim()
                  ? 'transparent'
                  : alpha(theme.palette.text.primary, 0.04),
                '&:hover': {
                  backgroundColor: inputValue.trim()
                    ? 'transparent'
                    : alpha(theme.palette.text.primary, 0.06),
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Suggested questions */}
        {showSuggestions && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              borderColor: 'divider',
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.background.paper, 0.82),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                <HelpOutlineIcon
                  fontSize="small"
                  sx={{ verticalAlign: 'middle', mr: 0.5 }}
                />
                Suggested questions
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer' }}
                onClick={toggleSuggestions}
              >
                Hide
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: isMobile ? 'center' : 'flex-start',
              }}
            >
              {suggestedQuestions.map((question) => (
                <Chip
                  key={question}
                  label={question}
                  onClick={() => handleSuggestedQuestion(question)}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    borderColor: alpha(theme.palette.primary.main, 0.24),
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}
        {!showSuggestions && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <Chip
              label="Show Suggested Prompts"
              onClick={toggleSuggestions}
              clickable
              sx={{
                borderRadius: 999,
                px: 1,
                fontWeight: 700,
                borderColor: alpha(theme.palette.primary.main, 0.26),
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.14),
                },
              }}
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Chat; 
